import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, ShoppingCart, FileText, IndianRupee, MapPin, Truck, PackageCheck, CheckCircle2, FileDown, Clock } from "lucide-react";
import AuditTrail from "../components/AuditTrail";
import POAttachments from "../components/POAttachments";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  ACTIVE: "bg-blue-50 text-blue-700 border-blue-200",
  DISPATCHED: "bg-cyan-50 text-cyan-700 border-cyan-200",
  IN_TRANSIT: "bg-amber-50 text-amber-700 border-amber-200",
  DELIVERED: "bg-teal-50 text-teal-700 border-teal-200",
  INSTALLED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
};

const trackingSteps = ["ACTIVE", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "INSTALLED", "COMPLETED"];
const trackingLabels = { ACTIVE: "PO Created", DISPATCHED: "Dispatched", IN_TRANSIT: "In Transit", DELIVERED: "Delivered", INSTALLED: "Installed", COMPLETED: "Completed" };

const Section = ({ icon: Icon, title, children }) => (
  <Card>
    <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-5">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 rounded-md bg-slate-900 text-white"><Icon className="w-3.5 h-3.5" /></div>
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

const InfoRow = ({ label, value }) => value ? (
  <div className="py-1.5">
    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</p>
    <p className="text-sm text-slate-900 font-medium mt-0.5">{value}</p>
  </div>
) : null;

function POView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [po, setPo] = useState(null);
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/purchase-orders/${id}`, getAuthHeader());
        setPo(r.data);
        if (r.data.requisition_id) {
          try {
            const rr = await axios.get(`${API_URL}/requisitions/${r.data.requisition_id}`, getAuthHeader());
            setReq(rr.data);
          } catch {}
        }
      } catch { toast.error("PO not found"); navigate("/purchase-orders"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API_URL}/purchase-orders/${id}/status?status=${status}`, {}, getAuthHeader());
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
      const r = await axios.get(`${API_URL}/purchase-orders/${id}`, getAuthHeader());
      setPo(r.data);
    } catch { toast.error("Failed to update"); }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/purchase-orders/${id}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a'); link.href = url; link.download = `${po.po_number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to generate PDF"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!po) return null;

  const currentStepIdx = po.status === "CANCELLED" ? -1 : trackingSteps.indexOf(po.status);
  const items = req?.items || [];

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="po-view-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/purchase-orders")}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900 font-mono">{po.po_number}</h1>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[po.status] || ""}`}>{po.status?.replace('_', ' ')}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Created {new Date(po.created_at || po.po_date).toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          {po.status === "ACTIVE" && <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => updateStatus("DISPATCHED")}>Mark Dispatched</Button>}
          {po.status === "DISPATCHED" && <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => updateStatus("IN_TRANSIT")}>Mark In Transit</Button>}
          {["DISPATCHED","IN_TRANSIT"].includes(po.status) && <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => updateStatus("DELIVERED")}>Mark Delivered</Button>}
          {po.status === "DELIVERED" && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => updateStatus("INSTALLED")}>Mark Installed</Button>}
          {["DELIVERED","INSTALLED"].includes(po.status) && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus("COMPLETED")}>Complete</Button>}
        </div>
      </div>

      {/* Tracking Timeline */}
      {po.status !== "CANCELLED" && (
        <Card>
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-5">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-md bg-slate-900 text-white"><Truck className="w-3.5 h-3.5" /></div>
              <CardTitle className="text-sm font-semibold">Delivery Tracking</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-200 z-0" />
              <div className="absolute top-4 left-0 h-0.5 bg-emerald-500 z-0 transition-all duration-500"
                style={{ width: currentStepIdx >= 0 ? `${(currentStepIdx / (trackingSteps.length - 1)) * 100}%` : '0%' }} />
              {trackingSteps.map((s, i) => {
                const isDone = i <= currentStepIdx;
                const isCurrent = i === currentStepIdx;
                return (
                  <div key={s} className="relative z-10 flex flex-col items-center" style={{ width: `${100 / trackingSteps.length}%` }}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      isDone ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 text-slate-400"
                    } ${isCurrent ? "ring-4 ring-emerald-100 scale-110" : ""}`}>
                      {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                    </div>
                    <p className={`text-xs mt-2 text-center font-medium ${isDone ? "text-emerald-700" : "text-slate-400"}`}>{trackingLabels[s]}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={ShoppingCart} title="Order Details">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="PO Number" value={po.po_number} />
            <InfoRow label="PO Date" value={po.po_date ? new Date(po.po_date).toLocaleDateString('en-IN') : null} />
            <InfoRow label="PO Value" value={po.po_value ? `₹${po.po_value.toLocaleString('en-IN')}` : null} />
            <InfoRow label="Proposal" value={po.proposal_number} />
            <InfoRow label="Department" value={req?.department} />
            <InfoRow label="Channel" value={req?.procurement_channel} />
          </div>
        </Section>

        <Section icon={MapPin} title="Vendor Details">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <InfoRow label="Name" value={po.vendor_details?.name} />
            <InfoRow label="GST" value={po.vendor_details?.gst_number} />
            <InfoRow label="PAN" value={po.vendor_details?.pan_number} />
            <InfoRow label="Contact" value={po.vendor_details?.contact_person} />
            <InfoRow label="Phone" value={po.vendor_details?.phone} />
            <InfoRow label="Email" value={po.vendor_details?.email} />
          </div>
          {po.vendor_details?.address && <div className="mt-2 p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Address</p><p className="text-sm mt-1">{po.vendor_details.address}</p></div>}
        </Section>
      </div>

      {items.length > 0 && (
        <Section icon={IndianRupee} title="Items">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12 font-semibold">#</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">HSN</TableHead>
                <TableHead className="font-semibold text-right">Qty</TableHead>
                <TableHead className="font-semibold text-right">Rate</TableHead>
                <TableHead className="font-semibold text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-mono text-xs text-slate-500">{item.serial_no || idx + 1}</TableCell>
                  <TableCell className="font-medium text-slate-900">{item.description}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{item.hsn_code || "—"}</TableCell>
                  <TableCell className="text-right">{item.quantity} {item.unit || "Nos"}</TableCell>
                  <TableCell className="text-right text-sm">₹{(item.estimated_cost || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-right font-semibold">₹{((item.quantity || 1) * (item.estimated_cost || 0)).toLocaleString('en-IN')}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-900 text-white">
                <TableCell colSpan={5} className="text-right font-semibold text-sm">PO Value</TableCell>
                <TableCell className="text-right font-bold text-lg">₹{(po.po_value || 0).toLocaleString('en-IN')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Section>
      )}

      <Section icon={FileText} title="Terms & Conditions">
        <div className="grid grid-cols-1 gap-3">
          {po.payment_terms && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 font-medium">Payment Terms</p><p className="text-sm mt-1">{po.payment_terms}</p></div>}
          {po.warranty_details && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 font-medium">Warranty</p><p className="text-sm mt-1">{po.warranty_details}</p></div>}
          {po.delivery_terms && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 font-medium">Delivery Terms</p><p className="text-sm mt-1">{po.delivery_terms}</p></div>}
        </div>
      </Section>

      {/* Tracking History */}
      {po.tracking_history && po.tracking_history.length > 0 && (
        <Section icon={Clock} title="Activity Log">
          <div className="space-y-3">
            {po.tracking_history.slice().reverse().map((ev, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900">{ev.status?.replace('_', ' ')}</p>
                  <p className="text-xs text-slate-500">{new Date(ev.timestamp).toLocaleString('en-IN')} — {ev.updated_by}</p>
                  {ev.remarks && <p className="text-xs text-slate-600 mt-1">{ev.remarks}</p>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Edit / Amendment Audit Trail */}
      <AuditTrail entries={po.audit_trail || []} title="Edit & Amendment History" />

      {/* PO Attachments — Annexure / GeM PO / CRAC etc. */}
      <POAttachments poId={po.id} isGem={(po.po_sub_type === "GEM") || /gem/i.test(po.procurement_channel || "")} />
    </div>
  );
}

export default POView;
